import { APP_URL, USERSROLE } from 'types';

type UserWithPreferences = {
  role?: string;
  preferences?: { user_type?: string };
};

type PendingLocation =
  | { pathname?: string; search?: string }
  | null
  | undefined;

export const hasResidentialUserType = (user?: UserWithPreferences): boolean =>
  user?.preferences?.user_type === 'Residential';

/** Residential app users must be Enquirer with user_type Residential. */
export const isResidentialUser = (
  user?: UserWithPreferences,
  userRole?: USERSROLE | string
): boolean =>
  (userRole || user?.role) === USERSROLE.ENQUIRER &&
  hasResidentialUserType(user);

/** Relative in-app paths only — blocks open redirects via ?redirectTo=. */
export const isSafeAppPath = (path?: string | null): boolean =>
  Boolean(
    path &&
      path.startsWith('/') &&
      !path.startsWith('//') &&
      !path.includes('://')
  );

/**
 * Maps a pending return path to a residential-app URL.
 * Accepts commercial `/enquiries`, `/enquiries/new`, `/enquiries/:id?...`,
 * already-residential paths, and email deep links (`src=email`) — not only
 * shareId links.
 */
export const toResidentialReturnUrl = (
  path?: string | null
): string | undefined => {
  if (!path || !isSafeAppPath(path)) {
    return undefined;
  }

  const [pathname, search] = path.split('?');
  const query = search ? `?${search}` : '';

  if (
    path === APP_URL.RESIDENTIAL ||
    path.startsWith(`${APP_URL.RESIDENTIAL}/`)
  ) {
    return path;
  }

  if (pathname === '/enquiries' || pathname === '/enquiries/') {
    return `${APP_URL.RESIDENTIAL}/enquiries${query}`;
  }

  if (
    pathname === APP_URL.LODGE_NEW_ENQUIRY ||
    pathname === `${APP_URL.LODGE_NEW_ENQUIRY}/`
  ) {
    return `${APP_URL.RESIDENTIAL}${APP_URL.LODGE_NEW_ENQUIRY}${query}`;
  }

  const enquiryId = /^\/enquiries\/(\d+)\/?$/.exec(pathname)?.[1];
  if (!enquiryId) {
    return undefined;
  }

  const params = new URLSearchParams(search);
  const detailQuery = params.toString();
  return detailQuery
    ? `${APP_URL.RESIDENTIAL}/enquiries/${enquiryId}?${detailQuery}`
    : `${APP_URL.RESIDENTIAL}/enquiries/${enquiryId}`;
};

/** Flattens a router `location.state.from` into a `pathname?search` string. */
export const toPendingPath = (location: PendingLocation): string | undefined =>
  location?.pathname
    ? `${location.pathname}${location.search || ''}`
    : undefined;

/** Parses `pathname?search` into a shape suitable for `history` state.from. */
export const toLocationShape = (
  path?: string | null
): { pathname: string; search: string } | undefined => {
  if (!path || !isSafeAppPath(path)) {
    return undefined;
  }
  const [pathname, search] = path.split('?');
  return {
    pathname,
    search: search ? `?${search}` : ''
  };
};

export const redirectResidentialEnquirerIfNeeded = (
  userRole: USERSROLE | string | undefined,
  user?: UserWithPreferences,
  pendingPath?: string | null
): boolean => {
  if (isResidentialUser(user, userRole)) {
    window.location.assign(
      toResidentialReturnUrl(pendingPath) || `${APP_URL.RESIDENTIAL}/`
    );
    return true;
  }
  return false;
};

/** Hard-navigate share links to the residential enquiry detail app when needed. */
export const redirectResidentialShareEnquiry = (
  pendingPath: string | undefined,
  user?: UserWithPreferences
): boolean => {
  if (!isResidentialUser(user) || !pendingPath || !isSafeAppPath(pendingPath)) {
    return false;
  }

  const [pathname, search] = pendingPath.split('?');
  const enquiryId = /^\/enquiries\/(\d+)\/?$/.exec(pathname)?.[1];
  const shareId = new URLSearchParams(search).get('shareId');
  if (!enquiryId || !shareId) {
    return false;
  }

  const shareUrl = toResidentialReturnUrl(pendingPath);
  if (!shareUrl) {
    return false;
  }
  window.location.assign(shareUrl);
  return true;
};
